import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, CheckCircle, Buildings, ChatCircle, ChartLine, DeviceMobile, Lightning, Globe, MagnifyingGlass, Sparkle
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import {
  SectionHero, SectionHeading, StatsRow, CTABanner, ProcessFlowSVG, FAQAccordion
} from '../components/SiteBlocks';
import { SERVICES } from '../data/content';

const ICONS = { Buildings, ChatCircle, ChartLine, DeviceMobile, Lightning, Globe, MagnifyingGlass };

export default function ServicesPage() {
  const { serviceId } = useParams();

  if (serviceId) {
    const service = SERVICES.find(s => s.id === serviceId);
    if (!service) {
      return (
        <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
          <SEO path={`/services/${serviceId}`} title="Service not found" />
          <Navbar />
          <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <h1 className="font-heading text-4xl font-bold mb-4">Service not found</h1>
            <Link to="/services"><Button style={{ background: 'var(--primary-accent)', color: '#fff' }}>Browse all services →</Button></Link>
          </div>
          <Footer />
        </div>
      );
    }
    return <ServiceDetailPage service={service} />;
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO
        path="/services"
        title="AI Agentic Workflow Services"
        description="Explore XceedAgents' 7 AI agent services: ERP/CRM agents, WhatsApp bots, analytics dashboards, mobile agents, product development, web development, and AI SEO."
      />
      <Navbar />

      <SectionHero
        eyebrow="Services"
        title="Agentic workflows for every part of your business."
        subtitle="From WhatsApp commerce to enterprise ERP automation to full-stack product development — we ship intelligent agents and full products with our hybrid AI + senior-engineer model."
      />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Sparkle;
              return (
                <Link key={s.id} to={`/services/${s.id}`} className="group block p-7 rounded-2xl border bg-white hover-lift transition-all hover:shadow-elevated" style={{ borderColor: 'var(--border)' }} data-testid={`service-${s.id}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'var(--tag-bg)' }}>
                    <Icon size={24} weight="duotone" style={{ color: 'var(--primary-accent)' }} />
                  </div>
                  <div className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: 'var(--primary-accent)' }}>{s.shortName}</div>
                  <h3 className="font-heading text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{s.name}</h3>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>{s.summary}</p>
                  <span className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: 'var(--primary-accent)' }}>
                    Explore <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABanner title="Don't see your exact need?" subtitle="Describe it. Our AI builds a custom proposal in 60 seconds." ctaText="Request for Proposal" ctaHref="/start-project" />
      <Footer />
    </div>
  );
}

function ServiceDetailPage({ service }) {
  const Icon = ICONS[service.icon] || Sparkle;
  const sd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.summary,
    "provider": { "@type": "Organization", "name": "XceedAgents.ai" },
    "areaServed": "Worldwide",
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO path={`/services/${service.id}`} title={service.name} description={service.summary} structuredData={sd} />
      <Navbar />

      <SectionHero
        eyebrow={service.shortName}
        title={service.tagline}
        subtitle={service.summary}
        testId={`service-hero-${service.id}`}
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/start-project"><Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="service-cta">Request for Proposal <ArrowRight size={16} weight="bold" /></Button></Link>
          <Link to="/services"><Button variant="outline" className="h-12 px-7 font-semibold" style={{ borderColor: '#fff', color: '#fff' }}>All Services</Button></Link>
        </div>
      </SectionHero>

      {/* Overview */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading eyebrow="Overview" title={`Why ${service.shortName} matters now`} />
          <div className="mt-6 text-lg leading-relaxed space-y-4 whitespace-pre-line" style={{ color: 'var(--text-primary)' }}>
            {service.overview}
          </div>
        </div>
      </section>

      {/* Key capabilities — big grid */}
      <section className="py-20 lg:py-24" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Capabilities" title="What we build" />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {service.key_capabilities.map((c, i) => (
              <div key={i} className="p-6 rounded-2xl border bg-white" style={{ borderColor: 'var(--border)' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: 'var(--tag-bg)' }}>
                  <Icon size={18} weight="duotone" style={{ color: 'var(--primary-accent)' }} />
                </div>
                <h4 className="font-heading text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{c.title}</h4>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process flow */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Process" title="How we deliver" />
          <div className="mt-10">
            <ProcessFlowSVG steps={service.process.map(p => p.step)} />
          </div>
          <div className="mt-12 grid md:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {service.process.map((p, i) => (
              <div key={i}>
                <div className="font-heading text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{p.step}</div>
                <div className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <StatsRow stats={service.outcomes.map(o => ({ value: o.metric, label: o.label }))} />

      {/* Integrations + Industries */}
      <section className="py-20 lg:py-24" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading eyebrow="Integrations" title="Plays well with your stack" />
            <div className="mt-6 flex flex-wrap gap-2">
              {service.integrations.map((s, i) => (
                <span key={i} className="px-3 py-1.5 text-sm rounded-md font-medium" style={{ background: 'var(--tag-bg)', color: 'var(--primary-accent)' }}>{s}</span>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading eyebrow="Industries" title="Where we've shipped this" />
            <div className="mt-6 flex flex-wrap gap-2">
              {service.industries.map((s, i) => (
                <span key={i} className="px-3 py-1.5 text-sm rounded-md border font-medium" style={{ borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}>{s}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <SectionHeading eyebrow="FAQs" title="Common questions" />
          <div className="mt-10">
            <FAQAccordion items={service.faqs} />
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="py-16" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="More" title="Explore other agent services" />
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {SERVICES.filter(s => s.id !== service.id).slice(0, 6).map(s => {
              const SIcon = ICONS[s.icon] || Sparkle;
              return (
                <Link key={s.id} to={`/services/${s.id}`} className="block p-6 rounded-xl border bg-white hover-lift" style={{ borderColor: 'var(--border)' }}>
                  <SIcon size={24} weight="duotone" style={{ color: 'var(--primary-accent)' }} className="mb-3" />
                  <h4 className="font-heading font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{s.name}</h4>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{s.summary?.slice(0, 100)}…</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABanner title={`Ready to ship ${service.name}?`} subtitle="Tell our AI what you need. Detailed proposal in 60 seconds." ctaText="Request for Proposal" ctaHref="/start-project" />
      <Footer />
    </div>
  );
}
